<template>
  <div class="character-profile">
    <div v-if="loading" class="loading">
      Loading character...
    </div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <div v-else-if="!character" class="error">
      Character not found
    </div>

    <div v-else class="profile-content" :class="{ 'isSubscribed': isSubscribed }">
      <!-- Header with logo -->
      <div class="profile-header">
        <div class="logo">
          <div class="text-logo">
            <div class="logo-icon">
              <span>G</span>
            </div>
            <div class="logo-text">GIRLS <span>inSYNC</span></div>
          </div>
        </div>
      </div>

      <!-- Profile info section -->
      <div class="profile-info">
        <!-- Back button -->
        <div class="back-button">
          <button @click="$router.go(-1)">
            <span>&lt;</span>
          </button>
        </div>
        
        <!-- Main profile image -->
        <div class="profile-main-image">
          <img :src="coverImage" :alt="character.name">
        </div>
        
        <!-- Profile details -->
        <div class="profile-details">
          <div class="profile-avatar">
            <img :src="character.avatar" :alt="character.name">
          </div>
          
          <div class="profile-name">
            <h1>{{ character.name }}</h1>
            <p class="username">@{{ characterUsername }}</p>
          </div>
          
          <div class="profile-actions">
            <button class="action-button qr-button">
              <span class="icon">⊞</span>
            </button>
          </div>
        </div>
        
        <!-- Bio section -->
        <div class="profile-bio">
          <p>{{ character.description || 'Hi there! I\'m ' + character.name }}</p>
        </div>
        
        <!-- Action buttons -->
        <div class="profile-buttons">
          <button class="tip-button">
            <span class="icon">$</span>
            Send Tip
          </button>
          <button class="subscribe-button" @click="handleSubscribe" :disabled="subscriptionLoading">
            <span class="icon">⭐</span>
            {{ isSubscribed ? 'Subscribed' : 'Subscribe' }}
          </button>
        </div>
        
        <!-- Tabs -->
        <div class="profile-tabs">
          <div 
            class="tab" 
            :class="{ 'active': activeTab === 'photo' }" 
            @click="activeTab = 'photo'"
          >Photo</div>
          <div 
            class="tab" 
            :class="{ 'disabled': !isSubscribed, 'active': activeTab === 'video' }" 
            @click="isSubscribed && (activeTab = 'video')"
          >Video</div>
        </div>
      </div>
      
      <!-- Gallery section -->
      <div class="profile-gallery" v-if="activeTab === 'photo'">
        <div class="gallery-grid">
          <div 
            v-for="(image, index) in galleryImages" 
            :key="index" 
            class="gallery-item"
            @click="handleImageClick(image)"
          >
            <div class="image-container" :class="{ 'locked': image.locked, 'premium': image.premium_only }">
              <img :src="image.image" :alt="character.name">
              <div v-if="image.locked" class="lock-icon">
                <span>🔒</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Video section - only visible when subscribed and video tab is active -->
      <div class="profile-video" v-if="activeTab === 'video' && isSubscribed">
        <div class="video-content">
          <div class="video-message">
            <div class="video-icon">🎬</div>
            <h3>Premium Videos</h3>
            <p>Access exclusive video content from {{ character.name }}</p>
            <!-- Placeholder for actual video content -->
            <div class="video-placeholder">
              <div class="coming-soon">Coming Soon</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Lightbox -->
      <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="close-btn" @click="closeLightbox">&times;</button>
          
          <div class="lightbox-image">
            <img :src="currentImage.image" :alt="character.name">
          </div>
        </div>
      </div>
      
      <!-- Fixed chat button -->
      <div class="chat-button-container">
        <button class="chat-button" @click="startChat">
          <span class="icon">💬</span>
          Start chatting
        </button>
      </div>
      
      <!-- Subscription Modal -->
      <SubscriptionModal 
        :show="showSubscriptionModal" 
        @close="closeSubscriptionModal"
        @select="handleSubscriptionSelect"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import '@/assets/logo.css'
import axios from 'axios'
import SubscriptionModal from '@/components/SubscriptionModal.vue'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

export default {
  name: 'CharacterDetail',
  components: {
    SubscriptionModal
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      lightboxOpen: false,
      currentImageIndex: 0,
      subscriptionLoading: false,
      activeTab: 'photo',
      showSubscriptionModal: false
    }
  },
  computed: {
    ...mapGetters('characters', ['currentCharacter', 'loading', 'error']),
    ...mapGetters('auth', ['user', 'isUserSubscribed']),
    character() {
      return this.currentCharacter
    },
    coverImage() {
      if (this.character) {
        return this.character.cover_image || this.character.avatar || '';
      }
      return '';
    },
    characterUsername() {
      if (this.character) {
        return this.character.username || 
               this.character.name?.toLowerCase().replace(/\s+/g, '') || 
               'user';
      }
      return 'user';
    },
    galleryImages() {
      // If character has gallery images, use those
      if (this.character && this.character.images && this.character.images.length > 0) {
        return this.character.images.map(img => ({
          ...img,
          locked: img.locked || false,
          // Make sure premium_only is available as a property
          premium_only: img.premium_only || false
        }))
      }
      
      // Otherwise, create a demo gallery with the character's avatar and cover image
      const images = [
        { 
          id: 'avatar',
          image: this.character ? this.character.avatar : '',
          locked: false,
          premium_only: false
        }
      ];
      
      // Add cover image to gallery if it's different from avatar
      if (this.character && this.character.cover_image && this.character.cover_image !== this.character.avatar) {
        images.push({
          id: 'cover',
          image: this.character.cover_image,
          locked: false,
          premium_only: false
        });
      }
      
      // Add a locked image
      images.push({ 
        id: 'locked-1',
        image: this.character ? (this.character.avatar || '') : '',
        locked: true,
        premium_only: false
      });
      
      return images;
    },
    currentImage() {
      return this.galleryImages[this.currentImageIndex] || {}
    },
    isSubscribed() {
      return this.isUserSubscribed;
    }
  },
  methods: {
    ...mapActions('characters', ['fetchCharacter']),
    ...mapActions('conversations', ['createConversation']),
    ...mapActions('auth', ['checkSubscriptionStatus']),
    
    handleImageClick(image) {
      // If image is locked, return
      if (image.locked) return;
      
      // If image is premium and user is not subscribed, show subscription modal
      if (image.premium_only && !this.isSubscribed) {
        this.showSubscriptionModal = true;
        return;
      }
      
      // Otherwise, open the lightbox
      this.openLightbox(image);
    },
    
    openLightbox(image) {
      this.currentImageIndex = this.galleryImages.findIndex(img => img.id === image.id)
      this.lightboxOpen = true
      
      // Prevent scrolling when lightbox is open
      document.body.style.overflow = 'hidden'
    },
    
    closeLightbox() {
      this.lightboxOpen = false
      
      // Re-enable scrolling
      document.body.style.overflow = ''
    },
    
    async startChat() {
      try {
        // Check if conversation already exists or create a new one
        const conversation = await this.createConversation(this.character.id)
        if (conversation) {
          this.$router.push({ name: 'Chat', params: { id: conversation.id } })
        }
      } catch (error) {
        console.error('Error starting chat:', error)
      }
    },
    
    handleSubscribe() {
      if (this.subscriptionLoading) return;
      
      // If already subscribed, return
      if (this.isSubscribed) {
        this.$toast.info('You already have an active subscription');
        return;
      }
      
      // Show the subscription modal instead of direct payment
      this.showSubscriptionModal = true;
    },
    
    closeSubscriptionModal() {
      this.showSubscriptionModal = false;
    },
    
    async handleSubscriptionSelect(subscriptionType) {
      if (this.subscriptionLoading) return;
      
      this.subscriptionLoading = true;
      this.showSubscriptionModal = false;
      
      try {
        // Check if Telegram WebApp is available
        if (!window.Telegram || !window.Telegram.WebApp) {
          throw new Error('Telegram WebApp is not available');
        }
        
        const telegram = window.Telegram.WebApp;
        
        // Map subscription type to period and price
        let subscriptionData = { period: '1 month' };
        
        switch(subscriptionType) {
          case 'monthly':
            subscriptionData = {
              period: 'month_1',
              amount: 1100
            };
            break;
          case 'quarterly':
            subscriptionData = {
              period: 'month_3',
              amount: 2700
            };
            break;
          case 'yearly':
            subscriptionData = {
              period: 'month_12',
              amount: 9600
            };
            break;
          default:
            subscriptionData = {
              period: 'month_3',
              amount: 1100
            };
        }
        
        // Call backend to initiate subscription payment
        const response = await axios.post(`${API_URL}/subscriptions/create/`, subscriptionData);
        
        if (response.data && response.data.invoiceLink) {
          // Open Telegram Stars payment page
          telegram.openInvoice(response.data.invoiceLink, async (status) => {
            if (status === 'paid') {
              // Check subscription status using Vuex action
              await this.checkSubscriptionStatus(); 
              
              // Check the result from the store
              if (this.isSubscribed) { 
                this.$toast.success(`Successfully subscribed to premium access`);
              } else {
                // Handle case when payment went through but system hasn't updated status
                this.$toast.info('Payment received, subscription is being processed...');
                
                // Add repeated checks using Vuex action
                const checkInterval = setInterval(async () => {
                  try {
                    // Call Vuex action to check status
                    await this.checkSubscriptionStatus(); 
                    if (this.isSubscribed) { // Check the updated Vuex state
                      this.$toast.success(`Successfully subscribed to premium access`);
                      clearInterval(checkInterval);
                    }
                  } catch (error) {
                    console.error('Error checking subscription status:', error);
                  }
                }, 5000); // Check every 5 seconds
                
                // Stop checking after 30 seconds to avoid infinite polling
                setTimeout(() => {
                  clearInterval(checkInterval);
                  // Optionally inform the user if status still not updated after timeout
                  if (!this.isSubscribed) {
                     this.$toast.info('Subscription update is taking longer than expected. Please check back later.');
                  }
                }, 30000);
              }
            } else {
              this.$toast.error('Payment was not completed');
            }
            this.subscriptionLoading = false;
          });
        } else {
          throw new Error('Failed to create subscription invoice');
        }
      } catch (error) {
        console.error('Subscription error:', error);
        this.$toast.error(error.message || 'Failed to process subscription');
        this.subscriptionLoading = false;
      }
    },
  },
  created() {
    this.fetchCharacter(this.id);
  },
}
</script>

<style scoped>
.character-profile {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
  position: relative;
  padding-bottom: 80px; /* Space for fixed chat button */
}

.loading, .error {
  text-align: center;
  padding: 20px;
  color: var(--tg-theme-hint-color, #999999);
}

.error {
  color: #e53935;
}

/* Header with logo */
.profile-header {
  background-color: #000000;
  padding: 10px 15px;
  display: flex;
  align-items: center;
}

.logo {
  height: 40px;
}

.logo img {
  height: 100%;
}

/* Profile info section */
.profile-info {
  position: relative;
}

.back-button {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
}

.back-button button {
  background-color: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
}

.profile-main-image {
  width: 100%;
  height: 50vh;
  max-height: 400px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
}

.profile-main-image::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  background: linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0));
}

.profile-main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.profile-details {
  display: flex;
  align-items: center;
  padding: 15px;
  position: relative;
  z-index: 3;
  margin-top: -20px;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #ffffff;
  margin-right: 15px;
  margin-top: -40px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-name {
  flex: 1;
}

.profile-name h1 {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
}

.username {
  margin: 0;
  color: var(--tg-theme-hint-color, #999999);
  font-size: 16px;
}

.profile-actions {
  display: flex;
}

.action-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
}

.profile-bio {
  padding: 0 15px 15px;
}

.profile-buttons {
  display: flex;
  padding: 0 15px 15px;
  gap: 10px;
}

.tip-button, .subscribe-button {
  flex: 1;
  padding: 12px;
  border-radius: 25px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  gap: 8px;
}

.tip-button {
  background-color: #f0f0f0;
  color: #000000;
}

.subscribe-button {
  background-color: #6C5CE7;
  color: #ffffff;
  transition: background-color 0.3s ease;
}

.subscribe-button:hover {
  background-color: #5A4BD1;
}

.subscribe-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* When subscribed, change the button appearance */
.isSubscribed .subscribe-button {
  background-color: #4CAF50; /* Green color for subscribed state */
}

.icon {
  font-size: 18px;
}

/* Tabs */
.profile-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 10px;
}

.tab {
  flex: 1;
  text-align: center;
  padding: 15px;
  font-weight: bold;
  cursor: pointer;
}

.tab.active {
  border-bottom: 2px solid #ff69b4;
  color: #ff69b4;
}

.tab.disabled {
  color: var(--tg-theme-hint-color, #999999);
  cursor: not-allowed;
}

/* When tab is both active and subscribed */
.tab.active.disabled {
  border-bottom: none;
  color: var(--tg-theme-hint-color, #999999);
}

/* Gallery */
.profile-gallery {
  padding: 15px;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.gallery-item {
  cursor: pointer;
}

.image-container {
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-container.locked {
  filter: blur(8px);
}

.lock-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 30px;
  color: #ffffff;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.3);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container.premium {
  border: 3px solid #9c88ff;
  box-sizing: border-box;
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.lightbox-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 30px;
  color: #ffffff;
  cursor: pointer;
  z-index: 10;
}

.lightbox-image img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

/* Fixed chat button */
.chat-button-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 15px;
  background-color: var(--tg-theme-bg-color, #ffffff);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.chat-button {
  width: 100%;
  padding: 15px;
  border-radius: 25px;
  border: none;
  background-color: #ff69b4;
  color: #ffffff;
  font-weight: bold;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

/* Video section */
.profile-video {
  padding: 15px;
}

.video-content {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.video-message {
  text-align: center;
}

.video-icon {
  font-size: 40px;
  margin-bottom: 15px;
  color: #ff69b4;
}

.video-message h3 {
  font-size: 22px;
  margin: 0 0 10px 0;
  color: #333;
}

.video-message p {
  color: var(--tg-theme-hint-color, #777);
  margin-bottom: 20px;
}

.video-placeholder {
  margin-top: 20px;
  height: 200px;
  background-color: #f0f0f0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ddd;
}

.coming-soon {
  color: var(--tg-theme-hint-color, #999999);
  font-size: 18px;
  font-weight: bold;
}
</style> 